/**
 * Dynamic Tab Renderer Test Page
 *
 * This page demonstrates the DynamicTabRenderer component
 * with full tab navigation and section rendering.
 */

import React, { useState } from 'react';
import DynamicTabRenderer from '../../components/dynamic/DynamicTabRenderer';

const DynamicFormSectionTest: React.FC = () => {
  // Simulate user role for testing
  const [userRole, setUserRole] = useState<string>('admin');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Test Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Dynamic Tab Renderer Test
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Testing the complete tab navigation system with DynamicTabRenderer
          </p>

          {/* User Role Selector */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Test as User Role:
            </label>
            <select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="user">User</option>
              <option value="guest">Guest</option>
            </select>
          </div>
        </div>

        {/* Dynamic Tab Renderer */}
        <DynamicTabRenderer
          applicationName="cybersweep"
          pageId="cybersweep_client_detail"
          recordId="CS-2024-001"
          userRole={userRole}
        />

        {/* Testing Info */}
        <div className="mt-8 bg-gray-100 dark:bg-gray-800 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Testing Instructions</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Tab Navigation:</strong> Click different tabs to switch between content</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Active Highlighting:</strong> Active tab shows purple underline</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Role-Based Visibility:</strong> Change user role to see different tabs (some tabs may be hidden)</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Section Routing:</strong> Each section automatically uses the correct renderer</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Form Editing:</strong> Hover over field values to see edit icons</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Field Actions:</strong> Click edit, then Save or Cancel to test field updates</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Collapsible Sections:</strong> Click section headers to expand/collapse</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Responsive Layout:</strong> Resize window to see 2-column → 1-column adaptation</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✅</span>
              <span><strong>Loading State:</strong> Refresh page to see loading spinner</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">📋</span>
              <span><strong>Future Features:</strong> Table, widget, and related-list sections show "Coming Soon"</span>
            </li>
          </ul>

          <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Component Architecture</h4>
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <p><strong>DynamicTabRenderer</strong> → Manages tab navigation and role-based visibility</p>
              <p className="ml-4">↓ renders sections via</p>
              <p className="ml-4"><strong>DynamicSectionRenderer</strong> → Routes to appropriate renderer based on type</p>
              <p className="ml-8">↓ for type='form' uses</p>
              <p className="ml-8"><strong>DynamicFormSection</strong> → Renders editable form fields</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicFormSectionTest;
